/** client/src/components/DossierChat.js **/

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

export default function DossierChat({ projectId, projectTitle }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamBuffer, setStreamBuffer] = useState('');
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (projectId) loadHistory();
    return () => abortRef.current?.abort();
  }, [projectId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamBuffer]);

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/projects/${projectId}/chat`, { withCredentials: true });
      setMessages(res.data);
    } catch (err) {
      console.error('Failed to load chat history', err);
    }
  };

  const clearHistory = async () => {
    if (!window.confirm('Clear chat history for this dossier?')) return;
    try {
      await axios.delete(`${API_URL}/api/projects/${projectId}/chat`, { withCredentials: true });
      setMessages([]);
    } catch (err) {
      console.error('Failed to clear history', err);
    }
  };

  const sendMessage = useCallback(async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text, created_at: new Date().toISOString() }]);
    setStreaming(true);
    setStreamBuffer('');

    abortRef.current = new AbortController();

    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: text }),
        signal: abortRef.current.signal
      });

      if (!response.ok) throw new Error('Chat request failed');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.delta) {
              accumulated += data.delta;
              setStreamBuffer(accumulated);
            }
            if (data.done) {
              setMessages(prev => [...prev, {
                role: 'assistant',
                content: accumulated,
                created_at: new Date().toISOString()
              }]);
              setStreamBuffer('');
              setStreaming(false);
            }
          } catch {}
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Chat stream error', err);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: '⚠️ Failed to get a response. Please try again.',
          created_at: new Date().toISOString()
        }]);
      }
      setStreamBuffer('');
      setStreaming(false);
    }

    inputRef.current?.focus();
  }, [input, streaming, projectId]);

  if (!projectId || projectId === 'all') {
    return (
      <div className="flex flex-col h-full items-center justify-center text-gray-400 text-sm p-4 text-center">
        <div className="text-3xl mb-2">💬</div>
        Select a dossier to chat with its documents.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-white">
        <div>
          <div className="text-xs font-semibold text-gray-700">Talk to the dossier</div>
          {projectTitle && <div className="text-[10px] text-gray-400 truncate max-w-[160px]">{projectTitle}</div>}
        </div>
        <button
          onClick={clearHistory}
          className="text-[10px] text-gray-400 hover:text-red-500"
          title="Clear chat history"
        >
          Clear
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 text-sm">
        {messages.length === 0 && !streaming && (
          <div className="text-center text-gray-400 text-xs mt-8">
            Ask questions, find trends, or compare documents in this dossier.
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[90%] rounded-lg px-3 py-2 text-xs whitespace-pre-wrap ${
                msg.role === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}

        {/* Streaming bubble */}
        {streaming && (
          <div className="flex justify-start">
            <div className="max-w-[90%] rounded-lg px-3 py-2 text-xs bg-gray-100 text-gray-800 whitespace-pre-wrap">
              {streamBuffer || <span className="animate-pulse text-gray-400">Thinking…</span>}
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="border-t p-2 flex gap-2">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Ask about this dossier…"
          disabled={streaming}
          className="flex-1 border rounded px-2 py-1 text-xs disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={streaming || !input.trim()}
          className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700 disabled:opacity-50"
        >
          {streaming ? '…' : 'Send'}
        </button>
      </form>
    </div>
  );
}
